import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, MapPin } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import ProductCardText, { ProductCardSkeleton } from "../components/ProductCardText";
import EmptyState from "../components/EmptyState";
import SuggestProductModal from "../components/SuggestProductModal";
import { toast } from "sonner";

export default function BrowsePage({ category }) {
  const { city, setCity, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const [debounced, setDebounced] = useState(initialQ);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data.categories || [])); }, []);

  // Keep URL in sync with the search box (so tag clicks via URL also pre-fill)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(id);
  }, [q]);

  // Reflect URL ?q= changes (e.g. when a tag link is clicked elsewhere)
  useEffect(() => {
    const fromUrl = searchParams.get("q") || "";
    if (fromUrl !== q) setQ(fromUrl);
    // eslint-disable-next-line
  }, [searchParams]);

  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 60;

  useEffect(() => {
    setLoading(true);
    setHasMore(true);
    const params = { limit: PAGE_SIZE, skip: 0 };
    if (category) params.category = category;
    if (city) params.city = city;
    if (debounced) params.q = debounced;
    // mirror q to URL so clicks are shareable
    if (debounced && debounced !== (searchParams.get("q") || "")) {
      setSearchParams({ q: debounced }, { replace: true });
    } else if (!debounced && searchParams.get("q")) {
      setSearchParams({}, { replace: true });
    }
    api.get("/products", { params }).then((r) => {
      const items = r.data.products || [];
      setProducts(items);
      setHasMore(items.length === PAGE_SIZE);
      setLoading(false);
    });
    // eslint-disable-next-line
  }, [category, city, debounced]);

  // Live refresh when bhega:changed fires anywhere in the app.
  useEffect(() => {
    const onChange = async (ev) => {
      const pid = ev?.detail?.productId;
      if (!pid) return;
      try {
        const r = await api.get(`/products/${pid}`, { params: city ? { city } : {} });
        const fresh = r.data;
        if (!fresh?.id) return;
        setProducts((cur) => cur.map((p) => (p.id === fresh.id ? { ...p, ...fresh } : p)));
      } catch (_) { /* noop */ }
    };
    window.addEventListener("bhega:changed", onChange);
    return () => window.removeEventListener("bhega:changed", onChange);
  }, [city]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const params = { limit: PAGE_SIZE, skip: products.length };
    if (category) params.category = category;
    if (city) params.city = city;
    if (debounced) params.q = debounced;
    try {
      const r = await api.get("/products", { params });
      const items = r.data.products || [];
      // Dedupe across pages
      const seen = new Set(products.map((p) => p.id));
      const fresh = items.filter((p) => !seen.has(p.id));
      setProducts((prev) => [...prev, ...fresh]);
      setHasMore(items.length === PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  };

  // IntersectionObserver-based infinite scroll
  useEffect(() => {
    if (loading || !hasMore) return;
    const sentinel = document.getElementById("browse-sentinel");
    if (!sentinel) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "400px" });
    io.observe(sentinel);
    return () => io.disconnect();
    // eslint-disable-next-line
  }, [loading, hasMore, products.length, debounced, category, city]);

  // Auto-detect city on first visit if user hasn't picked one yet
  useEffect(() => {
    if (city || !navigator.geolocation) return;
    const already = sessionStorage.getItem("bhega_loc_asked");
    if (already) return;
    sessionStorage.setItem("bhega_loc_asked", "1");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        // zoom=10 returns city/district granularity (not suburb/neighbourhood)
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=10&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`);
        const d = await r.json();
        const a = d.address || {};
        // Prefer broader city-level keys; never fall back to suburb / neighbourhood / hamlet / road
        const cityName =
          a.city ||
          a.town ||
          a.municipality ||
          a.city_district ||
          a.state_district ||
          a.county ||
          "";
        const state = a.state || "";
        if (cityName && state) {
          setCity(`${cityName}, ${state}`);
          toast.success(`Showing products available in ${cityName}, ${state}`);
        } else if (state) {
          setCity(state);
          toast.success(`Showing products available in ${state}`);
        }
      } catch { /* ignore */ }
      setLocating(false);
    }, () => setLocating(false), { timeout: 8000, maximumAge: 300000 });
  }, [city, setCity]);

  const applyTagToSearch = (tag) => {
    setQ(tag);
  };

  return (
    <div className="container-x py-8 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="eyebrow">{category ? "Category" : "Explore rooms"}</div>
          <h1 className="t-page mt-1" data-testid="browse-title">
            {category
              ? `${products[0]?.category_label || category}${(city && category !== "real_estate") ? ` in ${city.split(",")[0]}` : ""}`
              : (city ? `Everything available in ${city.split(",")[0]}` : "Browse all products")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5" data-testid="browse-count">
            {loading ? "Finding rooms…" : `${products.length} room${products.length === 1 ? "" : "s"}${debounced ? ` matching "${debounced}"` : ""} · tap ✊ Bhega to join buyers`}
          </p>
        </div>
      </div>

      <div className="mt-5 -mx-1 px-1 flex items-center gap-1.5 overflow-x-auto no-scrollbar" role="tablist" aria-label="Categories" data-testid="browse-category-chips">
        <Link to={debounced ? `/browse?q=${encodeURIComponent(debounced)}` : "/browse"} role="tab" aria-selected={!category} className={`chip shrink-0 ${!category ? "chip-active" : ""}`} data-testid="browse-cat-all">All</Link>
        {cats.map((c) => (
          <Link key={c.slug} to={`/${c.slug}`} role="tab" aria-selected={category === c.slug} className={`chip shrink-0 ${category === c.slug ? "chip-active" : ""}`} data-testid={`browse-cat-${c.slug}`}>
            {c.label} <span className="num opacity-60">{c.count}</span>
          </Link>
        ))}
      </div>

      <div className="mt-3 flex flex-col md:flex-row gap-2 items-stretch">
        <label className="flex-1 flex items-center gap-2.5 px-3.5 min-h-[48px] border border-[var(--bhega-border-strong)] bg-card focus-within:border-[var(--bhega-border-strong)] focus-within:shadow-[0_0_0_3px_var(--bhega-yellow-glow)] transition-[border-color,box-shadow]">
          <Search size={18} className="text-muted-foreground shrink-0" />
          <span className="sr-only">Search products</span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search brand, product, variant or tag — e.g. 'Creta SUV', 'iPhone'"
            className="flex-1 min-w-0 outline-none text-[15px] bg-transparent placeholder:text-[var(--bhega-faint)]"
            data-testid="browse-search-input"
            autoFocus
          />
          {q && (
            <button onClick={() => setQ("")} className="btn-ghost text-xs" data-testid="browse-search-clear">Clear</button>
          )}
        </label>
        <div className="shrink-0 flex items-center gap-2">
          {locating && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><MapPin size={12} className="pulse-dot" />detecting…</span>}
        </div>
      </div>

      {loading ? (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="browse-loading">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <ProductCardSkeleton key={i} />)}
        </div>
      ) : products.length === 0 ? (
        <div className="mt-6" data-testid="browse-empty">
          <EmptyState
            glyph="🔍"
            title={debounced ? `No rooms for “${debounced}” yet` : "No rooms here yet"}
            body={user ? "Can't find it? Suggest the product and an admin will open a room for it." : "Sign in to suggest a new product for the community."}
          >
            {user && <button onClick={() => setSuggestOpen(true)} className="btn-primary" data-testid="suggest-product-btn">+ Suggest to add this product</button>}
          </EmptyState>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 stagger">
            {Array.from(new Map(products.map((p) => [p.id, p])).values()).map((p) => <ProductCardText key={p.id} product={p} onTagClick={applyTagToSearch} />)}
          </div>
          {hasMore && (
            <div id="browse-sentinel" className="mt-8 text-center text-sm text-muted-foreground py-6" data-testid="browse-sentinel">
              {loadingMore ? "Loading more rooms…" : "Scroll for more"}
            </div>
          )}
          {!hasMore && products.length > PAGE_SIZE && (
            <div className="mt-8 text-center text-xs text-muted-foreground py-6">You&apos;ve reached the end · {products.length} rooms</div>
          )}
        </>
      )}

      <SuggestProductModal
        open={suggestOpen}
        defaultQuery={debounced}
        onClose={() => setSuggestOpen(false)}
      />
    </div>
  );
}
